import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom } from 'rxjs';
import { ErrorFrontend } from '@core/errors';
import type { PaginatedResponse, SupervisorCheck, SupervisorCode } from '@core/models';

/**
 * Supervisor (regente) Service
 *
 * The four digits the regente dictates to authorise a high-cost line, and the
 * record of what they have decided.
 *
 * The code is DERIVED from their own secret and the hour, never stored, so this
 * asks for it again rather than caching it: the backend recomputes it, the same
 * way the endpoint that validates it does, and the two can never drift apart.
 */
@Injectable({ providedIn: 'root' })
export class SupervisorService {
  private readonly http = inject(HttpClient);

  /** The code showing right now, and the seconds left in its window. */
  async code(): Promise<SupervisorCode> {
    return await firstValueFrom(
      this.http.get<SupervisorCode>('auth/supervisor_code/').pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /** What they have been asked, newest first. */
  async checks(page = 1): Promise<PaginatedResponse<SupervisorCheck>> {
    return await firstValueFrom(
      this.http
        .get<PaginatedResponse<SupervisorCheck>>('audits/supervisor_checks/', {
          params: { page },
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );
  }
}
